import React, { useEffect, useState, useCallback } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  required,
  useCreate,
  useRedirect,
  useNotify,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import {
  TableChart as TableIcon,
  LocationOn as ZoneIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';

const TablesCreate = () => {
  // Estado para almacenar el parámetro preseleccionado
  const [preselectedZoneId, setPreselectedZoneId] = useState<string | null>(
    null
  );

  // Estado para el filtro jerárquico
  const [filterValues, setFilterValues] =
    useState<HierarchicalFilterSelections>({});

  // Callback memoizado para el cambio de filtro
  const handleFilterChange = useCallback(
    (selections: HierarchicalFilterSelections) => {
      setFilterValues(selections);
    },
    []
  );

  // Obtener parámetros de la URL usando window.location de forma segura
  useEffect(() => {
    // Para hash routing, necesitamos obtener los parámetros del hash
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      const queryString = hash.substring(queryIndex + 1);
      const urlParams = new URLSearchParams(queryString);
      const zoneId = urlParams.get('zoneId');
      setPreselectedZoneId(zoneId);

      // Si hay una zona preseleccionada, establecerla en el filtro
      if (zoneId) {
        setFilterValues({ zoneId: parseInt(zoneId) });
      }
    }
  }, []);

  // Hooks para manejar la creación personalizada
  const [create] = useCreate();
  const redirect = useRedirect();
  const notify = useNotify();

  // Función personalizada para manejar el envío del formulario
  const handleSave = async (data: any) => {
    try {
      // Usar la zona seleccionada del filtro o la preseleccionada
      const zoneId =
        filterValues.zoneId ||
        (preselectedZoneId ? parseInt(preselectedZoneId) : undefined);

      if (!zoneId) {
        notify('Por favor selecciona una zona antes de crear la tabla', {
          type: 'error',
        });
        return;
      }

      const payload = {
        ...data,
        zoneId,
      };

      console.log('Payload enviado:', payload);

      await create('tables', { data: payload });

      notify('Tabla creada exitosamente', { type: 'success' });

      // Redirección personalizada
      const redirectTo = zoneId ? `/zones/${zoneId}/show` : '/tables';

      redirect(redirectTo);
    } catch (error) {
      console.error('Error al crear tabla:', error);
      notify('Error al crear la tabla', { type: 'error' });
    }
  };

  return (
    <Create redirect={false}>
      <Box sx={{ padding: 3, maxWidth: 800, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display='flex' alignItems='center' mb={3}>
              <TableIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Crear Nueva Tabla
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Agrega una nueva tabla al sistema
                </Typography>
              </Box>
            </Box>

            {/* Filtro Jerárquico para seleccionar planta y zona */}
            <Card
              sx={{
                p: 3,
                mb: 3,
                boxShadow: 2,
                backgroundColor: 'background.default',
              }}
            >
              <Typography variant='h6' gutterBottom color='primary'>
                Seleccionar Planta y Zona
              </Typography>
              <HierarchicalFilter
                levels={['plant', 'zone']}
                onChange={handleFilterChange}
                initialValues={filterValues}
                size='medium'
              />

              {!filterValues.zoneId && (
                <Alert severity='warning' sx={{ mt: 2 }}>
                  Debes seleccionar una planta y una zona antes de crear la
                  tabla
                </Alert>
              )}

              {filterValues.zoneId && (
                <Alert severity='success' sx={{ mt: 2 }}>
                  ✓ Selección completada: {filterValues.plantName} →{' '}
                  {filterValues.zoneName}
                </Alert>
              )}
            </Card>

            <SimpleForm
              onSubmit={handleSave}
              sx={{
                '& .RaSimpleForm-toolbar': {
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 2,
                },
              }}
            >
              <Grid container spacing={3}>
                {/* Información Principal */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <TableIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Información de la Tabla
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <TextInput
                        source='tableName'
                        label='Nombre de la Tabla'
                        validate={[required()]}
                        fullWidth
                        sx={{ mb: 2 }}
                        helperText='Nombre descriptivo para la tabla'
                        disabled={!filterValues.zoneId}
                      />

                      {!filterValues.zoneId && (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ fontStyle: 'italic' }}
                        >
                          Selecciona una planta y zona arriba para habilitar
                          este campo
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </SimpleForm>
          </CardContent>
        </Card>
      </Box>
    </Create>
  );
};

export default TablesCreate;
